import { getLocale, translate, translateRich, useLocale } from "../localization";
import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import API from "../services/API";
import { AppButton } from "../components/ui";
import { useContent } from "../context/ContentContext";
import { useExperiences } from "../context/ExperiencesContext";
import CollectionExperiencesSection from "../components/collections/CollectionExperiencesSection";
import CollectionElementsSection from "../components/collections/CollectionElementsSection";
import CollectionEnvironmentsSection from "../components/collections/CollectionEnvironmentsSection";
import CollectionSystemsSection from "../components/collections/CollectionSystemsSection";
import CollectionCollectionsSection from "../components/collections/CollectionCollectionsSection";
import "./CollectionPage.scss";
import "./Experiences.scss";
import "./ElementsPage.scss";
import "./EnvironmentsPage.scss";
import "./SystemsPage.scss";
import "./CollectionsPage.scss";

const CollectionItemType = {
  None: 0,
  Experience: 1,
  Element: 2,
  Environment: 3,
  System: 4,
  Skybox: 5,
  Collection: 6,
};

const getItemName = (item) => item?.name || translate("No Name");

const formatDate = (value) => {
  if (!value) return translate("Unknown");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale());
};

const sortItemsByOrder = (items) => {
  return [...items].sort((a, b) => {
    const aOrder = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;
    if (aOrder === bOrder) {
      return getItemName(a).localeCompare(getItemName(b));
    }
    return aOrder - bOrder;
  });
};

const groupItemsByType = (items) => {
  const grouped = {
    experiences: [],
    elements: [],
    environments: [],
    systems: [],
    collections: [],
    other: [],
  };

  sortItemsByOrder(items).forEach((item) => {
    switch (item?.type) {
      case CollectionItemType.Experience:
        grouped.experiences.push(item);
        break;
      case CollectionItemType.Element:
        grouped.elements.push(item);
        break;
      case CollectionItemType.Environment:
        grouped.environments.push(item);
        break;
      case CollectionItemType.System:
        grouped.systems.push(item);
        break;
      case CollectionItemType.Collection:
        grouped.collections.push(item);
        break;
      default:
        grouped.other.push(item);
        break;
    }
  });

  return grouped;
};

const resolveCollectionItems = (collectionItems, sourceItems, sourceIdField, fallbackBuilder) => {
  const lookup = new Map((sourceItems || []).map((sourceItem) => [sourceItem?.[sourceIdField], sourceItem]));

  return collectionItems.map((collectionItem) => {
    const sourceItem = lookup.get(collectionItem.id);
    return sourceItem || fallbackBuilder(collectionItem);
  });
};

const CollectionPage = () => {
  const locale = useLocale();
  const { collectionId } = useParams();
  const navigate = useNavigate();

  const { myElements, myEnvironments, mySystems, myCollections } = useContent();
  const { experiences } = useExperiences();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collection, setCollection] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!collectionId) return;

    let isMounted = true;

    const fetchCollection = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await API.get(`collections/${collectionId}`, {
          requiresAuth: true,
        });

        const payload = response?.data?.data || response?.data || {};
        const metadata = payload?.metadata || {};
        const items = Array.isArray(payload?.items) ? payload.items : [];

        if (!isMounted) return;

        setCollection({
          id: payload?.id || metadata?.id || collectionId,
          name: payload?.name || metadata?.name || translate("Untitled Collection"),
          description: payload?.description ?? metadata?.description ?? "",
          lastModified:
            payload?.lastModified ||
            metadata?.dateModified ||
            payload?.dateModified ||
            "",
          items,
        });
      } catch (fetchError) {
        console.error("Error fetching collection:", fetchError);
        if (isMounted) {
          setError(translate("Unable to load this collection."));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCollection();

    return () => {
      isMounted = false;
    };
  }, [collectionId, locale]);

  const groupedItems = useMemo(() => groupItemsByType(collection?.items || []), [collection?.items, locale]);

  const resolvedExperiences = useMemo(() => {
    return resolveCollectionItems(
      groupedItems.experiences,
      experiences || [],
      "experienceId",
      (item) => ({
        experienceId: item.id,
        name: item.name || translate("No Name"),
        systemId: "",
        guideId: "",
      })
    ).map((experience) => ({
      ...experience,
      experienceId: experience.experienceId || experience.id,
      name: experience.name || experience.metadata?.name || translate("No Name"),
      systemId: experience.systemId || "",
      guideId: experience.guideId || "",
    }));
  }, [groupedItems.experiences, experiences, locale]);

  const resolvedElements = useMemo(
    () =>
      resolveCollectionItems(
        groupedItems.elements,
        myElements || [],
        "id",
        (item) => ({
          id: item.id,
          name: item.name || translate("No Name"),
          description: item.description || translate("No Description"),
          status: {},
        })
      ),
    [groupedItems.elements, myElements, locale]
  );

  const resolvedEnvironments = useMemo(
    () =>
      resolveCollectionItems(
        groupedItems.environments,
        myEnvironments || [],
        "id",
        (item) => ({
          id: item.id,
          name: item.name || translate("No Name"),
          description: item.description || translate("No Description"),
          status: {},
        })
      ),
    [groupedItems.environments, myEnvironments, locale]
  );

  const resolvedSystems = useMemo(
    () =>
      resolveCollectionItems(
        groupedItems.systems,
        mySystems || [],
        "id",
        (item) => ({
          id: item.id,
          name: item.name || translate("No Name"),
          description: item.description || translate("No Description"),
          status: {},
        })
      ),
    [groupedItems.systems, mySystems, locale]
  );

  const resolvedCollections = useMemo(
    () =>
      resolveCollectionItems(
        groupedItems.collections,
        myCollections || [],
        "id",
        (item) => ({
          id: item.id,
          name: item.name || translate("No Name"),
          description: item.description || translate("No Description"),
          status: {},
        })
      ),
    [groupedItems.collections, myCollections, locale]
  );

  if (loading) {
    return (
      <div className="collectionDetailPage">
        <div className="collectionDetailLoading">{translate("Loading...")}</div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="collectionDetailPage">
        <div className="collectionDetailError">{error || translate("Collection not found.")}</div>
        <AppButton kind="secondary" onClick={() => navigate("/collections")}>{translate("Back to Collections")}</AppButton>
      </div>
    );
  }

  const hasRenderableItems =
    groupedItems.experiences.length > 0 ||
    groupedItems.elements.length > 0 ||
    groupedItems.environments.length > 0 ||
    groupedItems.systems.length > 0 ||
    groupedItems.collections.length > 0;

  return (
    <div className="collectionDetailPage">
      <div className="collectionDetailHeader">
        <AppButton kind="ghost" onClick={() => navigate("/collections")}>{translate("Back to Collections")}</AppButton>

        <Typography className="collectionDetailTitle" variant="h4">
          {collection.name}
        </Typography>
        <Typography className="collectionDetailMeta">{translateRich("ID: {value0}", {
  value0: collection.id
})}</Typography>
        <Typography className="collectionDetailMeta">{translateRich("Last Updated: {value0}", {
  value0: formatDate(collection.lastModified)
})}</Typography>
        <Typography className="collectionDetailDescription">
          {collection.description || translate("No Description")}
        </Typography>
      </div>

      {groupedItems.experiences.length > 0 && (
        <CollectionExperiencesSection experiences={resolvedExperiences} />
      )}

      {groupedItems.elements.length > 0 && (
        <CollectionElementsSection elements={resolvedElements} />
      )}

      {groupedItems.environments.length > 0 && (
        <CollectionEnvironmentsSection environments={resolvedEnvironments} />
      )}

      {groupedItems.systems.length > 0 && (
        <CollectionSystemsSection systems={resolvedSystems} />
      )}

      {groupedItems.collections.length > 0 && (
        <CollectionCollectionsSection collections={resolvedCollections} />
      )}

      {!hasRenderableItems && (
        <div className="collectionDetailEmpty">{translate("This collection has no supported items yet.")}</div>
      )}
    </div>
  );
};

export default CollectionPage;
